import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap } from 'lucide-react';
import { selectionModalVariants, contentVariants } from './utils/animations';
import RecommendationForm from './components/RecommendationForm';
import { useClarityModalTracking } from '../../hooks/useClarityModalTracking';
import { trackEvent, trackJourneyStep } from '../../config/clarity';

/**
 * RecommendationModal component
 * Replaces the SelectionModal when recommendation is requested
 * Walks through recommendation questions with the user
 */
const RecommendationModal = ({
  isOpen,
  onClose,
  onComplete = () => {}
}) => {
  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [backdropClickEnabled, setBackdropClickEnabled] = React.useState(false);
  const modalRef = useRef(null);
  
  // Track modal open/close
  const { trackAction } = useClarityModalTracking('recommendation_modal', isOpen, {
    isMobile: windowWidth < 768
  });
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      // Disable backdrop click for the first 500ms to prevent accidental closing
      setBackdropClickEnabled(false);
      const timer = setTimeout(() => {
        setBackdropClickEnabled(true);
        console.log("Backdrop click enabled for recommendation modal");
      }, 500);
      
      return () => {
        // Restore scrolling when component unmounts or modal closes
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
        
        // Clear the timer
        clearTimeout(timer);
      };
    }
  }, [isOpen]);
  
  // Handle completion of the recommendation process
  const handleComplete = (recommendedSpeed) => {
    // Track recommendation completion
    trackEvent('recommendation_completed', {
      recommendedSpeed,
      isMobile
    });
    
    // Track journey step
    trackJourneyStep('package_selection', 'received_recommendation', {
      recommendedSpeed
    });
    
    // Call the onComplete callback with the recommended speed
    onComplete(recommendedSpeed);
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - only on mobile or when not side-by-side with comparison modal */}
          {isMobile && (
            <motion.div
              className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
              style={{ zIndex: 59 }} // Higher z-index for backdrop, but below the modal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                // Only close if backdrop click is enabled and the click is directly on the backdrop
                if (backdropClickEnabled) {
                  console.log("Backdrop clicked, closing recommendation modal");
                  // Track backdrop close
                  trackAction('backdrop_close');
                  onClose();
                } else {
                  console.log("Backdrop click ignored - too soon after opening");
                }
              }}
            />
          )}
          
          {/* Modal */}
          <motion.div
            ref={modalRef}
            className={`fixed top-0 bottom-0 right-0 bg-gradient-to-b from-gray-900 to-gray-800 shadow-2xl border-l border-gray-800 flex flex-col ${
              isMobile ? 'w-full' : 'w-1/4'
            }`}
            style={{
              zIndex: 60, // Higher z-index than SelectionModal to ensure it's on top
              width: isMobile ? '100%' : 'calc(25% - 1px)',
              boxShadow: '0 0 15px rgba(0, 0, 0, 0.3)' /* Add stronger shadow for better visibility */
            }}
            variants={selectionModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => {
              // Stop propagation to prevent clicks from bubbling up to the backdrop
              e.stopPropagation();
            }}
          >
            {/* Header */}
            <motion.div 
              className="p-4 border-b border-gray-800 flex justify-between items-center"
              variants={contentVariants}
            >
              <div className="flex items-center">
                <Zap className="h-5 w-5 text-purple-400 mr-2" />
                <h2 className="text-xl font-bold text-white">Speed Recommendation</h2>
              </div>
              <button 
                className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors"
                onClick={() => {
                  // Track X button close
                  trackAction('x_button_close');
                  onClose();
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </motion.div>
            
            {/* Recommendation Form */}
            <div className="flex-1 overflow-y-auto p-4">
              <RecommendationForm onComplete={handleComplete} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RecommendationModal;